/**
 * fteDemand.ts
 * Pure math for the Scenario Phase-3 "rough demand" strip (Jul-23 meeting
 * heuristic): a quote's value ÷ the blended junior rate ≈ person-hours the job
 * embeds; ÷ 40 h ≈ FTE-weeks. Deliberately rough — lab pricing is largely
 * labor-driven, but machine-heavy tests overstate people-time, so callers
 * present this as a floor-check, never a target.
 */

export interface FteDemand {
  /** Quote value converted to CAD (Subtotal × FX rate). */
  quoteValueCad: number;
  /** quoteValueCad ÷ hourlyRate. */
  personHours: number;
  /** personHours ÷ hoursPerWeek, rounded to 1 decimal. */
  demandFteWeeks: number;
}

/**
 * Demand from a quote's pre-tax Subtotal (discount/PM-fee/tax are revenue
 * adjustments, not work). `fxRate` converts the quote currency to CAD
 * (Quote.FxRateAtQuote — 1.0 for CAD, null treated as 1).
 * Returns null when there's nothing meaningful to divide.
 */
export function computeFteDemand(
  subtotal: number | null | undefined,
  fxRate: number | null | undefined,
  hourlyRate: number,
  hoursPerWeek: number,
): FteDemand | null {
  if (!subtotal || subtotal <= 0 || hourlyRate <= 0 || hoursPerWeek <= 0) return null;
  const quoteValueCad = subtotal * (fxRate && fxRate > 0 ? fxRate : 1);
  const personHours = quoteValueCad / hourlyRate;
  return {
    quoteValueCad,
    personHours: Math.round(personHours),
    demandFteWeeks: Math.round((personHours / hoursPerWeek) * 10) / 10,
  };
}

/**
 * Planned people-time across the project window: Σ of the per-week team totals
 * (each week's Σ of shares, 0–N×100) ÷ 100, rounded to 1 decimal. The same
 * numbers the "Team need" row prints, summed.
 */
export function plannedFteWeeks(
  teamTotalByWeek: ReadonlyMap<string, number>,
  projectWeekColumns: readonly string[],
): number {
  let sum = 0;
  for (const ws of projectWeekColumns) sum += teamTotalByWeek.get(ws) ?? 0;
  return Math.round(sum / 10) / 10;
}
